import React, { useState, useEffect } from 'react';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import styles from './EmployeeList.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import employeeService from '../../services/employeeService';
import InputField from '../../components/common/InputField/InputField';
import { Employee } from '../../types/employeeTypes';
import { useAuth } from '../../hooks/useAuth';

const EmployeeList: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Employee',
    is_active: false,
    canManageCustomers: false,
    canManageInvoices: false,
    canManageEmployees: false,
    canViewReports: false,
    canCreateEmployee: false,
    canEditEmployee: false,
    canManageSettings: false,
    canManageDocuments: false,
    storage_space_allocation: 0
  });
  // const [viewDetailsModal, setViewDetailsModal] = useState(false);
  // const [employeeDetails, setEmployeeDetails] = useState<{ email: string; password?: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState<string | null>(null);
  const { auth } = useAuth();

  const fetchEmployees = async () => {
    try {
      const response = await employeeService.getEmployees();
      // Ensure each employee has default properties
      const normalizedEmployees = Array.isArray(response)
        ? response
            .map(emp => {
              if (!emp || typeof emp !== 'object') {
                console.warn('Invalid employee object:', emp);
                return null;
              }

              return {
                id: emp.id || '',
                name: emp.name || '',
                email: emp.email || '',
                phone_number: emp.phone_number || '',
                role: emp.role || 'employee',
                is_active: emp.is_active !== undefined ? emp.is_active : true,
                permissions: typeof emp.permissions === 'object' ? emp.permissions : {
                  canManageCustomers: false,
                  canManageInvoices: false,
                  canManageEmployees: false,
                  canViewReports: false,
                  canCreateEmployee: false,
                  canEditEmployee: false,
                  canManageSettings: false,
                  canManageDocuments: false,
                },
                storage_space_allocation: emp.storage_space_allocation || 0,
                createdAt: emp.createdAt || new Date().toLocaleDateString('en-CA'),
                updatedAt: emp.updatedAt || new Date().toLocaleDateString('en-CA'),
              };
            })
            .filter((emp): emp is Employee => emp !== null)
        : [];

      setEmployees(normalizedEmployees);

      console.log("Normalized employees:", normalizedEmployees);
    } catch (err) {
      console.error('Error fetching employees:', err);
      toast.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleAdd = async () => {
    // Frontend validation for required fields
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields: Name, Email, and Phone Number.');
      return;
    }
    const data = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      role: form.role === 'Admin' ? 'admin' : 'employee',
      is_active: true,
      permissions: {
        canManageCustomers: form.canManageCustomers,
        canManageInvoices: form.canManageInvoices,
        canManageEmployees: form.canManageEmployees,
        canViewReports: form.canViewReports,
        canCreateEmployee: form.canCreateEmployee,
        canEditEmployee: form.canEditEmployee,
        canManageSettings: form.canManageSettings,
        canManageDocuments: form.canManageDocuments,
        storage_space_allocation: form.storage_space_allocation
      },
      storage_space_allocation: form.storage_space_allocation
    };

    try {
      await employeeService.createEmployee(data); 
      toast.success('Employee created successfully!');
      setShowModal(false);
      setForm({
        name: '',
        email: '',
        phone: '',
        role: 'Employee',
        is_active: true,
        canManageCustomers: false,
        canManageInvoices: false,
        canManageEmployees: false,
        canViewReports: false,
        canCreateEmployee: false,
        canEditEmployee: false,
        canManageSettings: false,
        canManageDocuments: false,
        storage_space_allocation: 0
      });
      fetchEmployees(); 
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create employee';
      toast.error(message);
    }
  };

  // const handleDelete = async (id: string) => {
  //   try {
  //     await employeeService.deleteEmployee(id);
  //     toast.success('Employee deleted successfully!');
  //     fetchEmployees(); // Refresh the list
  //   } catch (err) {
  //     toast.error('Failed to delete employee');
  //   }
  // };

  const handleToggleActive = async (id: string) => {
    // Find the current employee
    const currentEmployee = employees.find(emp => emp.id === id);
    if (!currentEmployee) {
      toast.error('Employee not found');
      return;
    }

    // Calculate the new status - use nullish coalescing to handle undefined
    const currentStatus = currentEmployee.is_active ?? true;
    const newStatus = !currentStatus;
    
    // Optimistically update the UI first
    setEmployees(employees.map(emp =>
      emp.id === id ? { ...emp, is_active: newStatus } : emp
    ));

    try {
      // Make the API call
      const updatedEmployee = await employeeService.toggleEmployeeStatus(id);
      
      // Sync with server response if it provides the data
      if (updatedEmployee && typeof updatedEmployee === 'object') {
        setEmployees(prevEmployees => prevEmployees.map(emp => {
          if (emp.id === id) {
            return {
              ...emp,
              ...updatedEmployee,
              is_active: updatedEmployee.is_active ?? newStatus,
              permissions: updatedEmployee.permissions || emp.permissions || {}
            };
          }
          return emp;
        }));
      }
      
      toast.success('Employee status updated successfully!');
    } catch (err) {
      // Revert the optimistic update on error
      setEmployees(prevEmployees => prevEmployees.map(emp =>
        emp.id === id ? { ...emp, is_active: currentStatus } : emp
      ));
      
      const message = err instanceof Error ? err.message : 'Failed to update employee status';
      toast.error(message);
    }
  };

  const handleToggleCustomerAccess = async (id: string) => {
    const employee = employees.find(emp => emp.id === id);
    if (!employee) return;

    const newPermissions = {
      ...employee.permissions,
      canManageCustomers: !employee.permissions?.canManageCustomers,
    };
    try {
      // const updatedEmployee = await employeeService.updateEmployeePermissions(id, newPermissions);
    
    // ✅ Update employee in list instead of removing
    setEmployees(prev =>
      prev.map(emp =>
        emp.id === id ? { ...emp, permissions: newPermissions } : emp
      )
    );

      toast.success('Permission update successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update customer access';
      toast.error(message);
    }
  };

  const handleToggleInvoiceEdit = async (id: string) => {
    const employee = employees.find(emp => emp.id === id);
    if (!employee) return;

    const newPermissions = {
      ...employee.permissions,
      canManageInvoices: !employee.permissions?.canManageInvoices,
    };
    try {
      // const updatedEmployee = await employeeService.updateEmployeePermissions(id, newPermissions);
    
      // ✅ Update employee in list instead of removing
      setEmployees(prev =>
        prev.map(emp =>
          emp.id === id ? { ...emp, permissions: newPermissions } : emp
        )
      );
      toast.success('Permission update successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update invoice edit';
      toast.error(message);
    }
  };

  const handleToggleReceivedAmountEdit = async (id: string) => {
    const employee = employees.find(emp => emp.id === id);
    if (!employee) return;

    const newPermissions = {
      ...employee.permissions,
      canViewReports: !employee.permissions?.canViewReports,
    };
    try {
      // const updatedEmployee = await employeeService.updateEmployeePermissions(id, newPermissions);
    
      // ✅ Update employee in list instead of removing
      setEmployees(prev =>
        prev.map(emp =>
          emp.id === id ? { ...emp, permissions: newPermissions } : emp
        )
      );
      toast.success('Permission update successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update invoice edit';
      toast.error(message);
    }
  };

  // const handleViewDetails = async (id: string) => {
  //   try {
  //     const details = await employeeService.viewEmployeeDetails(id);
  //     setEmployeeDetails(details);
  //     setViewDetailsModal(true);
  //   } catch (err) {
  //     toast.error('Failed to fetch employee details');
  //   }
  // };

  // const handleCopy = (text: string) => {
  //   navigator.clipboard.writeText(text);
  //   toast.success('Copied to clipboard!');
  // };

  // const handleCopyAll = () => {
    // let text = `Email: ${employeeDetails?.email || ''}`;
    // if (employeeDetails?.password) {
    //   text += `\nPassword: ${employeeDetails.password}`;
    // }
    // navigator.clipboard.writeText(text);
  //   toast.success('Copied to clipboard!');
  // };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleOpenEditModal = (employee: Employee) => {
    setForm({
      name: employee.name || '',
      email: employee.email || '',
      phone: employee.phone_number || '',
      is_active: employee.is_active || false,
      role: employee.role === 'admin' ? 'Admin' : 'Employee',
      canManageCustomers: employee.permissions?.canManageCustomers || false,
      canManageInvoices: employee.permissions?.canManageInvoices || false,
      canManageEmployees: employee.permissions?.canManageEmployees || false,
      canViewReports: employee.permissions?.canViewReports || false,
      canCreateEmployee: employee.permissions?.canCreateEmployee || false,
      canEditEmployee: employee.permissions?.canEditEmployee || false,
      canManageSettings: employee.permissions?.canManageSettings || false,
        canManageDocuments: employee.permissions?.canManageDocuments || false,
        storage_space_allocation: employee.storage_space_allocation || 0
      });
    setEditingEmployeeId(employee.id);
    setEditMode(true);
    setShowModal(true);
  };

  const handleSaveEdit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields: Name, Email, and Phone Number.');
      return;
    }
    if (!editingEmployeeId) return;
    const data = {
      id: editingEmployeeId,
      name: form.name,
      email: form.email,
      phone: form.phone,
      is_active: form.is_active,
      role: form.role === 'Admin' ? 'admin' : 'employee',
      permissions: {
        canManageCustomers: form.canManageCustomers,
        canManageInvoices: form.canManageInvoices,
        canManageEmployees: form.canManageEmployees,
        canViewReports: form.canViewReports,
        canCreateEmployee: form.canCreateEmployee,
        canEditEmployee: form.canEditEmployee,
        canManageSettings: form.canManageSettings,
        canManageDocuments: form.canManageDocuments
      },
      storage_space_allocation: form.storage_space_allocation
    };
    try {
      await employeeService.updateEmployee(data);
      toast.success('Employee updated successfully!');
      setShowModal(false);
      setEditMode(false);
      setEditingEmployeeId(null);
      setForm({
        name: '',
        email: '',
        phone: '',
        is_active: true,
        role: 'Employee',
        canManageCustomers: false,
        canManageInvoices: false,
        canManageEmployees: false,
        canViewReports: false,
        canCreateEmployee: false,
        canEditEmployee: false,
        canManageSettings: false,
        canManageDocuments: false,
        storage_space_allocation: 0
      });
      fetchEmployees();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update employee';
      toast.error(message);
    }
  };

  const filteredEmployees = employees.filter(emp =>
    (emp.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (emp.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (emp.role?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.container}>
      <h2>Employees</h2>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.searchContainer}>
            <div className={styles.searchWrapper}>
              <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search employees by name, email, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  className={styles.clearButton}
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
        {role === 'admin' && (
          <Button variant="primary" onClick={handleOpenModal}>
            + Add Employee
          </Button>
        )}
      </div>
      </div>

      <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Role</th>
            {auth.user?.is_cloud_active && <th>Storage Space Allocation</th>}
            {!auth.user?.is_cloud_active && <th>Customer Access</th>}
            {!auth.user?.is_cloud_active && <th>Invoice Edit</th>}
            {!auth.user?.is_cloud_active && <th>Received Amount Edit</th>}
            {role === 'admin' && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={role === 'admin' ? 7 : 6} style={{ textAlign: 'center', padding: '20px' }}>
                Loading employees...
              </td>
            </tr>
          ) : (
            Array.isArray(filteredEmployees) && filteredEmployees.length > 0 ? (
              filteredEmployees.map(emp => {
                // Additional safety check for each employee
                if (!emp || !emp.id) {
                  console.warn('Invalid employee in render:', emp);
                  return null;
                }
                
                // Add safety checks for each property
                const isActive = emp.is_active !== undefined ? emp.is_active : true;
                const permissions = emp.permissions || {};
                
                return (
                  <tr key={emp.id} className={!isActive ? styles.inactive : ''}>
                    <td>{emp.name || '-'}</td>
                    <td>{emp.email || '-'}</td>
                    <td>{emp.phone_number || '-'}</td>
                    <td>{emp.role || 'employee'}</td>
                    {auth.user?.is_cloud_active && <td>{emp.storage_space_allocation || 0} GB</td>}
                    {!auth.user?.is_cloud_active && (
                      <>                    <td>
                      <label className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          checked={permissions.canManageCustomers || false}
                          onChange={() => handleToggleCustomerAccess(emp.id)}
                          aria-label={permissions.canManageCustomers ? 'Disable Customer Access' : 'Enable Customer Access'}
                        />
                        <span className={styles.slider}></span>
                      </label>
                    </td>
                    <td>
                      <label className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          checked={permissions.canManageInvoices || false}
                          onChange={() => handleToggleInvoiceEdit(emp.id)}
                          aria-label={permissions.canManageInvoices ? 'Disable Invoice Edit' : 'Enable Invoice Edit'}
                        />
                        <span className={styles.slider}></span>
                      </label>
                    </td>
                    <td>
                      <label className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          checked={permissions.canViewReports || false}
                          onChange={() => handleToggleReceivedAmountEdit(emp.id)}
                          aria-label={permissions.canViewReports ? 'Disable Received Amount Edit' : 'Enable Received Amount Edit'}
                        />
                        <span className={styles.slider}></span>
                      </label>
                    </td>
                    </>
                    )}
                    {role === 'admin' && (
                      <td>
                        <div className={styles.actionIcons}>
                          <Button
                            variant={isActive ? 'secondary' : 'success'}
                            className={styles.actionButton}
                            onClick={() => handleToggleActive(emp.id)}
                            aria-label={isActive ? 'Disable Employee' : 'Enable Employee'}
                          >
                            {isActive ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="7" r="4"></circle>
                                <path d="M5 21v-2a4 4 0 0 1 4-4h2"></path>
                                <polyline points="16 19 18 21 22 17"></polyline>
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="7" r="4"></circle>
                                <path d="M5 21v-2a4 4 0 0 1 4-4h2"></path>
                                <line x1="17" y1="17" x2="21" y2="21"></line>
                                <line x1="21" y1="17" x2="17" y2="21"></line>
                              </svg>
                            )}
                          </Button>
                          {/* <Button
                            onClick={() => handleDelete(emp.id)}
                            className={styles.actionButton}
                            aria-label="Delete Employee"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#ef4444"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          </Button> */}
                          {/* <Button */}
                            {/* onClick={() => handleViewDetails(emp.id)}
                            className={styles.actionButton}
                            aria-label="View Employee Details"
                          > */}
                            {/* Eye SVG icon */}
                            {/* <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            > */}
                              {/* <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </Button> */}
                          <Button
                            onClick={() => handleOpenEditModal(emp)}
                            className={styles.actionButton}
                            aria-label="Edit Employee"
                          >
                            {/* Pencil/Edit SVG icon */}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z"/></svg>
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              }).filter(row => row !== null)
            ) : (
              <tr>
                <td colSpan={role === 'admin' ? 7 : 6} style={{ textAlign: 'center', padding: '20px' }}>
                  {searchTerm ? 'No employees found matching your search' : 'No employees found'}
                </td>
              </tr>
            )
          )}
        </tbody>
      </table>
      </div>
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setEditMode(false); setEditingEmployeeId(null); }} title={editMode ? "Edit Employee" : "Add Employee"} size='lg'>
        <div className={styles.modalForm}>
          <InputField
            label="Name"
            name="name"
            type="text"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Employee Name"
            required
          />
          <InputField
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="Employee Email"
            required
          />
          <InputField
            label="Phone Number"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            placeholder="Phone Number"
            required
          />
          <label>Role</label>
          <div className={styles.selectField}>
            <select
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}
              className={styles.selectInput}
            >
              <option value="Employee">Employee</option>
              <option value="Admin">Admin</option>
            </select>
            <span className={styles.selectArrow}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 8 10 12 14 8" />
              </svg>
            </span>
          </div>
          {auth.user?.is_cloud_active && (
            
             <InputField
  label="Storage Space Allocation (GB)"
  name="storage_space_allocation"
  type="number"
  value={form.storage_space_allocation}
  onChange={e =>
    setForm({
      ...form,
      storage_space_allocation: Number(e.target.value) // 👈 convert to number
    })
  }
  placeholder="Storage Space Allocation (GB)"
  required
/>
          )}
          {!auth.user?.is_cloud_active && (
            <>
          <div className={styles.toggleRow}>
            <label className={styles.toggleLabel}>
              <span>Customer Access</span>
              <label className={styles.toggleSwitch}>
                <input
                  type="checkbox"
                  checked={form.canManageCustomers}
                  onChange={e => setForm({ ...form, canManageCustomers: e.target.checked })}
                />
                <span className={styles.slider}></span>
              </label>
            </label>
            <label className={styles.toggleLabel}>
              <span>Invoice Edit</span>
              <label className={styles.toggleSwitch}>
                <input
                  type="checkbox"
                  checked={form.canManageInvoices}
                  onChange={e => setForm({ ...form, canManageInvoices: e.target.checked })}
                />
                <span className={styles.slider}></span>
              </label>
            </label>
          </div>
          <div className={styles.toggleRow}>
            <label className={styles.toggleLabel}>
              <span>Received Amount Edit Access</span>
              <label className={styles.toggleSwitch}>
                <input
                  type="checkbox"
                  checked={form.canViewReports}
                  onChange={e => setForm({ ...form, canViewReports: e.target.checked })}
                />
                <span className={styles.slider}></span>
              </label>
            </label>
          </div>
          </>
          )}
          <Button variant="primary" onClick={editMode ? handleSaveEdit : handleAdd} className={styles.addButton}>
            {editMode ? "Save Changes" : "Add"}
          </Button>
        </div>
      </Modal>
      {/* <Modal isOpen={viewDetailsModal} onClose={() => setViewDetailsModal(false)} title="Employee Details">
        {employeeDetails && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <strong>Email:</strong> {employeeDetails.email}
            </div>
            {employeeDetails.password && (
              <div style={{ marginBottom: 12 }}>
                <strong>Password:</strong> {employeeDetails.password}
              </div>
            )}
            <div className={styles.copyButtonWrapper}>
              <Button
                className={styles.wideCopyButton}
                onClick={handleCopyAll}
              >
                Copy
              </Button>
            </div>
          </div>
        )}
      </Modal> */}
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default EmployeeList;
